/**
 * Built-in themes for Synesthesia editor
 */

export const solar = {
  name: 'solar',
  colors: {
    // Editor colors
    'bg-primary': '#faf0ca',
    'bg-secondary': '#ffffff',
    'text': '#0d3b66',
    'cursor': '#f95738',
    'selection': 'rgba(244, 211, 94, 0.4)',
    'scrollbar': '#d4c290',
    'scrollbar-thumb': '#b8a060',
    
    // Syntax tokens
    'token-comment': '#6a737d',
    'token-punctuation': '#586e75',
    'token-property': '#2aa198',
    'token-selector': '#2aa198',
    'token-operator': '#6c71c4',
    'token-keyword': '#859900',
    'token-function': '#268bd2',
    'token-variable': '#b58900',
    'token-string': '#cb4b16',
    'token-number': '#d33682',
    'token-boolean': '#b58900',
    'token-null': '#b58900',
    'token-class': '#268bd2',
    'token-tag': '#268bd2',
    'token-attribute': '#b58900',
    'token-regex': '#dc322f',
    'token-important': '#dc322f',
    
    // Syntax classes (for parser output)
    'syn-comment': '#6a737d',
    'syn-keyword': '#859900',
    'syn-string': '#cb4b16',
    'syn-number': '#d33682',
    'syn-function': '#268bd2',
    'syn-variable': '#b58900',
    'syn-operator': '#6c71c4',
    'syn-punctuation': '#586e75',
    'syn-class': '#268bd2',
    'syn-heading': '#268bd2',
  }
};

export const cave = {
  name: 'cave',
  colors: {
    // Editor colors
    'bg-primary': '#141E26',
    'bg-secondary': '#1D2D3E',
    'text': '#c5dde8',
    'cursor': '#f26419',
    'selection': 'rgba(51, 101, 138, 0.4)',
    'scrollbar': '#243442',
    'scrollbar-thumb': '#33658a',
    
    // Syntax tokens
    'token-comment': '#5c6370',
    'token-punctuation': '#abb2bf',
    'token-property': '#e06c75',
    'token-selector': '#e06c75',
    'token-operator': '#56b6c2',
    'token-keyword': '#c678dd',
    'token-function': '#61afef',
    'token-variable': '#d19a66',
    'token-string': '#98c379',
    'token-number': '#d19a66',
    'token-boolean': '#56b6c2',
    'token-null': '#56b6c2',
    'token-class': '#e5c07b',
    'token-tag': '#e06c75',
    'token-attribute': '#d19a66',
    'token-regex': '#56b6c2',
    'token-important': '#c678dd',
    
    // Syntax classes (for parser output)
    'syn-comment': '#5c6370',
    'syn-keyword': '#c678dd',
    'syn-string': '#98c379',
    'syn-number': '#d19a66',
    'syn-function': '#61afef',
    'syn-variable': '#d19a66',
    'syn-operator': '#56b6c2',
    'syn-punctuation': '#abb2bf',
    'syn-class': '#e5c07b',
    'syn-heading': '#e06c75',
  }
};

export const themes = {
  solar,
  cave,
  light: solar,
  dark: cave
};

export function getTheme(theme) {
  if (typeof theme === 'string') {
    const themeObj = themes[theme] || themes.solar;
    return { ...themeObj, name: theme };
  }
  return theme;
}

export function themeToCSSVars(colors, prefix = '') {
  return Object.entries(colors)
    .map(([key, value]) => {
      const varName = prefix ? `--${prefix}-${key}` : `--${key}`;
      return `${varName}: ${value};`;
    })
    .join('\n');
}

export function mergeTheme(baseTheme, customColors = {}) {
  const theme = {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      ...customColors
    }
  };
  
  // Auto-generate syn-* colors from token-* colors if not provided
  Object.entries(theme.colors).forEach(([key, value]) => {
    if (key.startsWith('token-')) {
      const synKey = key.replace('token-', 'syn-');
      if (!theme.colors[synKey]) {
        theme.colors[synKey] = value;
      }
    }
  });
  
  return theme;
}

/**
 * Create a custom theme
 * @param {string} name - Theme name
 * @param {object} colors - Theme colors
 * @returns {object} Theme object
 */
export function createTheme(name, colors) {
  const theme = {
    name,
    colors: {
      // Provide defaults for required colors
      'bg-primary': colors['bg-primary'] || '#ffffff',
      'bg-secondary': colors['bg-secondary'] || '#f5f5f5',
      'text': colors['text'] || '#000000',
      'cursor': colors['cursor'] || '#000000',
      'selection': colors['selection'] || 'rgba(0, 0, 0, 0.2)',
      ...colors
    }
  };
  
  // Auto-generate syn-* colors from token-* colors
  Object.entries(theme.colors).forEach(([key, value]) => {
    if (key.startsWith('token-')) {
      const synKey = key.replace('token-', 'syn-');
      if (!theme.colors[synKey]) {
        theme.colors[synKey] = value;
      }
    }
  });
  
  return theme;
}

/**
 * Apply theme CSS variables to an element
 * @param {HTMLElement} element - Target element
 * @param {object} theme - Theme object
 * @param {string} prefix - CSS variable prefix
 */
export function applyThemeToElement(element, theme, prefix = '') {
  const cssVars = themeToCSSVars(theme.colors, prefix);
  element.style.cssText += cssVars;
}

export default {
  solar,
  cave,
  getTheme,
  themeToCSSVars,
  mergeTheme,
  createTheme,
  applyThemeToElement
};
